import { useEffect, useState, useCallback } from "react";
import {
  listConversations,
  getConversation,
  addConversationNote,
  markConversationReviewed,
  composeConversationFollowup,
  convertConversationToContact,
  previewWeeklyDigest,
  sendWeeklyDigestNow,
} from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  MessageSquare, User as UserIcon, Landmark, DollarSign, AlertCircle,
  Send, CheckCircle2, UserPlus, StickyNote, Search, X, Mail, ExternalLink,
} from "lucide-react";

// Flag chip metadata — mirrored from server _compute_flags().
const FLAG_META = {
  has_lead:            { label: "Lead",       icon: UserIcon,   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  mentions_legislator: { label: "Legislator", icon: Landmark,   cls: "bg-blue-50 text-blue-700 border-blue-200" },
  mentions_bill:       { label: "Bill",       icon: MessageSquare, cls: "bg-purple-50 text-purple-700 border-purple-200" },
  mentions_donation:   { label: "Donation",   icon: DollarSign, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  urgent_language:     { label: "Urgent",     icon: AlertCircle,cls: "bg-red-50 text-red-700 border-red-200" },
};

function FlagChips({ flags }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {Object.entries(flags || {})
        .filter(([, v]) => v)
        .map(([k]) => {
          const m = FLAG_META[k];
          if (!m) return null;
          const Icon = m.icon;
          return (
            <span
              key={k}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${m.cls}`}
              data-testid={`flag-chip-${k}`}
            >
              <Icon size={12} />
              {m.label}
            </span>
          );
        })}
    </div>
  );
}

function ConversationDrawer({ sid, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [sending, setSending] = useState(false);

  const refresh = useCallback(async () => {
    if (!sid) return;
    setLoading(true);
    try {
      const d = await getConversation(sid);
      setData(d);
      if (d?.lead?.name && !composeSubject) {
        setComposeSubject(`Following up on your chat with the Colorado Catholic Conference`);
      }
    } catch {
      toast.error("Could not load conversation");
    } finally {
      setLoading(false);
    }
  }, [sid]);  // eslint-disable-line

  useEffect(() => { refresh(); }, [refresh]);

  const submitNote = async () => {
    if (!note.trim()) return;
    try {
      await addConversationNote(sid, note.trim());
      setNote("");
      toast.success("Note added");
      refresh();
      onChanged?.();
    } catch { toast.error("Could not save note"); }
  };

  const markReviewed = async () => {
    try {
      await markConversationReviewed(sid);
      toast.success("Marked reviewed");
      refresh();
      onChanged?.();
    } catch { toast.error("Could not mark reviewed"); }
  };

  const sendCompose = async () => {
    if (!composeSubject.trim() || !composeBody.trim()) {
      toast.error("Subject and body required");
      return;
    }
    setSending(true);
    try {
      // Convert plain-text newlines to <br/> for Resend HTML body.
      const html = composeBody
        .split("\n\n")
        .map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
        .join("");
      const r = await composeConversationFollowup(sid, composeSubject.trim(), html);
      toast.success(r.mocked ? "Email queued (Resend not configured — mocked)" : "Follow-up sent");
      setComposeBody("");
      refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const promoteToContact = async () => {
    try {
      const r = await convertConversationToContact(sid);
      toast.success(r.already_existed ? "Contact already existed" : "Contact created");
      refresh();
      onChanged?.();
    } catch { toast.error("Could not create contact"); }
  };

  return (
    <Sheet open={!!sid} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="conversation-drawer">
        <SheetHeader className="mb-4">
          <SheetTitle className="font-serif text-2xl text-ccc-plum">
            Conversation
          </SheetTitle>
          <SheetDescription className="text-xs text-stone-500 font-mono">{sid}</SheetDescription>
        </SheetHeader>

        {loading && <div className="text-stone-500 text-sm">Loading…</div>}

        {data && (
          <div className="space-y-6">
            {/* Meta */}
            <div className="border border-stone-200 bg-stone-50 p-4">
              <div className="flex flex-wrap items-center gap-3 justify-between mb-3">
                <div className="text-xs text-stone-600">
                  Started {data.created_at && formatDistanceToNow(new Date(data.created_at), { addSuffix: true })} · {data.turns} turns · {data.message_count} messages
                </div>
                <FlagChips flags={data.flags} />
              </div>
              {data.lead ? (
                <div className="text-sm bg-white border border-emerald-200 p-3">
                  <div className="text-xs uppercase tracking-wider text-emerald-700 mb-1 flex items-center gap-1"><UserIcon size={12}/>Lead captured</div>
                  <div className="font-medium text-stone-800" data-testid="lead-name">{data.lead.name}</div>
                  <div className="text-stone-600">{data.lead.email}</div>
                  {data.lead.parish && <div className="text-xs text-stone-500 mt-1">{data.lead.parish}</div>}
                </div>
              ) : (
                <div className="text-xs text-stone-500 italic">
                  No lead attached — visitor did not submit the widget's contact form.
                </div>
              )}
              {data.reviewed_at && (
                <div className="mt-3 inline-flex items-center gap-1 text-xs text-emerald-700">
                  <CheckCircle2 size={14} /> Reviewed by {data.reviewed_by} · {formatDistanceToNow(new Date(data.reviewed_at), { addSuffix: true })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {!data.reviewed_at && (
                <Button
                  variant="outline" size="sm" onClick={markReviewed}
                  data-testid="btn-mark-reviewed"
                >
                  <CheckCircle2 size={14} className="mr-1.5"/> Mark reviewed
                </Button>
              )}
              {!data.lead && (
                <Button
                  variant="outline" size="sm" onClick={promoteToContact}
                  data-testid="btn-promote-contact"
                >
                  <UserPlus size={14} className="mr-1.5"/> Add to CRM
                </Button>
              )}
            </div>

            {/* Transcript */}
            <div>
              <h3 className="text-xs uppercase tracking-widest text-ccc-purple mb-2">Transcript</h3>
              <div className="space-y-3 border border-stone-200 p-4 bg-white max-h-96 overflow-y-auto" data-testid="transcript">
                {data.messages?.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-ccc-purple text-white rounded-br-none"
                          : "bg-stone-100 text-stone-800 rounded-bl-none"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {(!data.messages || data.messages.length === 0) && (
                  <div className="text-stone-400 italic text-sm">No messages yet.</div>
                )}
              </div>
            </div>

            {/* Compose follow-up */}
            {data.lead && (
              <div>
                <h3 className="text-xs uppercase tracking-widest text-ccc-purple mb-2 flex items-center gap-1">
                  <Send size={12}/> Follow up
                </h3>
                <Input
                  value={composeSubject}
                  onChange={e => setComposeSubject(e.target.value)}
                  placeholder="Subject"
                  className="mb-2"
                  data-testid="compose-subject"
                />
                <Textarea
                  value={composeBody}
                  onChange={e => setComposeBody(e.target.value)}
                  rows={6}
                  placeholder={`Dear ${data.lead.name?.split(" ")[0] || "friend"},\n\nThanks for reaching out through our AI assistant. I noticed you were asking about…`}
                  data-testid="compose-body"
                />
                <div className="mt-2 flex justify-end">
                  <Button size="sm" onClick={sendCompose} disabled={sending} data-testid="btn-send-compose">
                    <Send size={14} className="mr-1.5"/>
                    {sending ? "Sending…" : "Send follow-up"}
                  </Button>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <h3 className="text-xs uppercase tracking-widest text-ccc-purple mb-2 flex items-center gap-1">
                <StickyNote size={12}/> Internal notes ({data.notes?.length || 0})
              </h3>
              <div className="space-y-2 mb-3">
                {(data.notes || []).map(n => (
                  <div key={n.id} className="border border-stone-200 bg-stone-50 p-3 text-sm">
                    <div className="text-stone-800 whitespace-pre-wrap">{n.body}</div>
                    <div className="text-xs text-stone-500 mt-1">
                      {n.author_name || n.author_email} · {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                    </div>
                  </div>
                ))}
                {(!data.notes || data.notes.length === 0) && (
                  <div className="text-xs text-stone-400 italic">No notes yet.</div>
                )}
              </div>
              <Textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={2}
                placeholder="Add an internal note (visible to CCC staff only)…"
                data-testid="note-input"
              />
              <div className="mt-2 flex justify-end">
                <Button size="sm" variant="outline" onClick={submitNote} data-testid="btn-add-note">
                  Add note
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const FILTERS = [
  { key: "all",             label: "All",         params: {} },
  { key: "leads",           label: "Leads only",  params: { has_lead: true } },
  { key: "urgent",          label: "Urgent",      params: { flag: "urgent_language" } },
  { key: "legislator",      label: "Legislator",  params: { flag: "mentions_legislator" } },
  { key: "bill",            label: "Bill",        params: { flag: "mentions_bill" } },
  { key: "donation",        label: "Donation",    params: { flag: "mentions_donation" } },
  { key: "unreviewed",      label: "Unreviewed",  params: { reviewed: false } },
];

function WeeklyDigestDialog({ open, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const p = await previewWeeklyDigest();
        if (!cancelled) setPreview(p);
      } catch {
        toast.error("Could not load digest preview");
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const sendNow = async () => {
    if (!window.confirm(`Send the weekly digest to ${preview?.recipients?.length || 0} staff now?\n\nAlready-sent recipients (same ISO week) will be skipped.`)) return;
    setSending(true);
    try {
      const r = await sendWeeklyDigestNow();
      toast.success(`Digest sent to ${r.weekly_digest ?? 0} staff.`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="weekly-digest-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-ccc-plum flex items-center gap-2">
            <Mail size={20}/> State of the CCC — Weekly Digest Preview
          </DialogTitle>
          <DialogDescription>
            This is exactly what will land in staff inboxes on Monday at 8 AM Denver.
            {preview?.recipients?.length ? (
              <span className="text-stone-600"> Sends to <strong>{preview.recipients.length}</strong>: {preview.recipients.join(", ")}</span>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="p-8 text-stone-500 text-sm text-center">Building preview…</div>}
        {preview && (
          <>
            <div className="text-sm text-stone-700 border-l-2 border-ccc-gold pl-3 py-1 mb-2">
              <span className="uppercase text-xs tracking-widest text-ccc-purple">Subject </span>
              <span className="font-medium">{preview.subject}</span>
            </div>
            <iframe
              srcDoc={preview.html}
              title="Weekly digest preview"
              className="flex-1 w-full border border-stone-200 bg-white min-h-[500px]"
              sandbox=""
              data-testid="digest-preview-iframe"
            />
          </>
        )}

        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-stone-100">
          <Button variant="outline" onClick={onClose} data-testid="btn-close-digest">Close</Button>
          <Button
            onClick={sendNow}
            disabled={sending || !preview}
            data-testid="btn-send-digest-now"
          >
            <Send size={14} className="mr-1.5"/>
            {sending ? "Sending…" : "Send now"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Conversations() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selectedSid, setSelectedSid] = useState(null);
  const [digestOpen, setDigestOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 50, ...(FILTERS.find(f => f.key === activeFilter)?.params || {}) };
      if (q.trim()) params.q = q.trim();
      const r = await listConversations(params);
      setSessions(r.sessions || []);
    } catch {
      toast.error("Could not load conversations");
    } finally {
      setLoading(false);
    }
  }, [activeFilter, q]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="admin-conversations-page">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-ccc-gold">AI Assistant</div>
          <h1 className="font-serif text-4xl text-ccc-plum">Conversations.</h1>
          <div className="text-sm text-stone-600 mt-1">
            Browse public visitor chats. Follow up with leads, spot bad AI replies, add internal notes.
            <span className="ml-1 text-stone-500">Non-lead sessions auto-purge after 90 days.</span>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDigestOpen(true)}
          data-testid="btn-preview-digest"
          className="flex-shrink-0"
        >
          <Mail size={14} className="mr-1.5"/> Preview weekly digest
        </Button>
      </div>

      {/* Filters + search */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setActiveFilter(f.key)}
            data-testid={`filter-${f.key}`}
            className={`px-3 py-1.5 text-xs border transition ${
              activeFilter === f.key
                ? "bg-ccc-plum text-white border-ccc-plum"
                : "bg-white text-stone-700 border-stone-300 hover:border-ccc-purple hover:text-ccc-purple"
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-2.5 top-2.5 text-stone-400"/>
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search transcripts…"
            className="pl-8 pr-8 w-64 h-9"
            data-testid="conversations-search"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-2 top-2.5 text-stone-400 hover:text-stone-600"
              aria-label="Clear search"
            >
              <X size={14}/>
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-stone-200">
        {loading && (
          <div className="p-8 text-center text-stone-400 text-sm">Loading conversations…</div>
        )}
        {!loading && sessions.length === 0 && (
          <div className="p-8 text-center text-stone-400 text-sm" data-testid="empty-conversations">
            No conversations match this filter.
          </div>
        )}
        {!loading && sessions.map(s => (
          <button
            key={s.session_id}
            onClick={() => setSelectedSid(s.session_id)}
            data-testid={`conversation-row-${s.session_id}`}
            className={`w-full text-left border-b border-stone-100 hover:bg-stone-50 p-4 flex gap-4 transition ${
              !s.reviewed_at ? "border-l-2 border-l-ccc-purple" : ""
            }`}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-ccc-plum/10 flex items-center justify-center text-ccc-plum">
              <MessageSquare size={18}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="text-sm font-medium text-stone-800 truncate">
                  {s.lead?.name || <span className="text-stone-500 italic">Anonymous visitor</span>}
                </div>
                {s.lead?.email && (
                  <span className="text-xs text-stone-500 truncate">{s.lead.email}</span>
                )}
                <span className="ml-auto text-xs text-stone-400 flex-shrink-0">
                  {s.last_at && formatDistanceToNow(new Date(s.last_at), { addSuffix: true })}
                </span>
              </div>
              <div className="text-sm text-stone-600 line-clamp-2 mb-1.5">
                {s.first_message_snippet || <span className="italic text-stone-400">No user messages yet</span>}
              </div>
              <div className="flex items-center gap-2 justify-between">
                <FlagChips flags={s.flags} />
                <span className="text-xs text-stone-400 flex-shrink-0">{s.turns} turns · {s.message_count} msgs</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      <ConversationDrawer
        sid={selectedSid}
        onClose={() => setSelectedSid(null)}
        onChanged={load}
      />

      <WeeklyDigestDialog open={digestOpen} onClose={() => setDigestOpen(false)} />
    </div>
  );
}
